import { useState, useEffect } from "react";
import LinearProgress from "@mui/material/LinearProgress";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Pagination from "@mui/material/Pagination";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import Tooltip from "@mui/material/Tooltip";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

// Replace these with your actual API hooks
import useGetExpiredUnresolvedOrdersApi from "../../API/useGetExpiredUnresolvedOrdersApi";
import useUpdateTaxiOrderApi from "../../API/useUpdateTaxiOrderApi";

const cairoFontStyle = `
  @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;600;700;800&display=swap');
  * { font-family: 'Cairo', sans-serif !important; }
`;

const statusMap = {
  ناقص: { label: "ناقص", color: "warning" },
  "مؤكد غير مدفوع": { label: "مؤكد غير مدفوع", color: "info" },
  "مؤكد مدفوع": { label: "مؤكد مدفوع", color: "success" },
  "تذكير أول": { label: "تذكير أول", color: "success" },
  "تذكير ثاني": { label: "تذكير ثاني", color: "success" },
  استبيان: { label: "استبيان", color: "success" },
  مكتمل: { label: "مكتمل", color: "success" },
  ملغي: { label: "ملغي", color: "error" },
};

const statusOptions = [
  { value: "ناقص", label: "ناقص" },
  { value: "مؤكد غير مدفوع", label: "مؤكد غير مدفوع" },
  { value: "مؤكد مدفوع", label: "مؤكد مدفوع" },
  { value: "تذكير أول", label: "تذكير أول" },
  { value: "تذكير ثاني", label: "تذكير ثاني" },
  { value: "استبيان", label: "استبيان" },
  { value: "مكتمل", label: "مكتمل" },
  { value: "ملغي", label: "ملغي" },
];

export default function TaxiExpiredOrders() {
  // Add this state near the other states
  const [saveErrors, setSaveErrors] = useState({});
  //
  const [page, setPage] = useState(1);
  const rowsPerPage = 12;

  useEffect(() => {
    window.scrollTo(0, 0);
    const styleEl = document.createElement("style");
    styleEl.innerHTML = cairoFontStyle;
    document.head.appendChild(styleEl);
    return () => document.head.removeChild(styleEl);
  }, []);

  const { data, isPending, fetchStatus } = useGetExpiredUnresolvedOrdersApi();
  const updateMutation = useUpdateTaxiOrderApi();

  // Per-card selected status state: { [orderId]: statusValue }
  const [selectedStatuses, setSelectedStatuses] = useState({});
  // Track which cards are saving
  const [savingIds, setSavingIds] = useState({});

  const [rows, setRows] = useState([]);

  const paginatedRows = rows.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  useEffect(() => {
    setPage(1);
  }, [rows.length]);

  const handlePageChange = (_, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const formatPhoneForWhatsApp = (phone) => {
    if (!phone) return null;
    const cleaned = phone.replace(/\D/g, "");
    let formatted = cleaned;
    if (formatted.startsWith("0")) formatted = formatted.substring(1);
    if (formatted.length === 9) formatted = "966" + formatted;
    return formatted;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const formatHour = (timeStr) => {
    if (!timeStr) return "—";
    if (!timeStr.includes(":")) return timeStr;
    const [hours, minutes] = timeStr.split(":").map(Number);
    const period = hours >= 12 ? "م" : "ص";
    const hours12 = hours % 12 || 12;
    return `${hours12}:${minutes.toString().padStart(2, "0")} ${period}`;
  };

  useEffect(() => {
    if (data?.data?.orders) {
      const formatted = data.data.orders.map((item, index, array) => ({
        id: item.id,
        index: array.length - index,
        order_number: item.order_number,
        type: item.type,
        extra_route_access: item.extra_route_access,
        car_model: item.car_model,
        seats_number: item.seats_number,
        price: item.price,
        name: item.name,
        phone: item.phone,
        whatsapp_number: formatPhoneForWhatsApp(item.phone),
        pickup_location: item.pickup_location,
        neighborhood: item.neighborhood,
        pickup_date: item.pickup_date,
        status: item.status,
        new_status: item.new_status,
        date: item.created_at,
        pickup_hour: item.pickup_hour,
        hotel_name: item.hotel_name,
        driver_name: item.driver_name,
        driver_phone_number: item.driver_phone_number,
        driver_amount: item.driver_amount,
        images: item.images || [],
      }));
      setRows(formatted);

      // Init selected statuses
      const initStatuses = {};
      formatted.forEach((r) => {
        initStatuses[r.id] = r.new_status;
      });
      setSelectedStatuses(initStatuses);
    }
  }, [data]);

  const openTicketImage = (images) => {
    if (!images || images.length === 0) return;
    window.open(images[0].full_path, "_blank", "noopener,noreferrer");
  };

  const handleStatusChange = (id, value) => {
    setSelectedStatuses((prev) => ({ ...prev, [id]: value }));
  };

  const handleSave = async (row) => {
    setSavingIds((prev) => ({ ...prev, [row.id]: true }));
    setSaveErrors((prev) => ({ ...prev, [row.id]: null })); // clear previous error
    try {
      await updateMutation.mutateAsync({ id: row.id, new_status: selectedStatuses[row.id] });
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, new_status: selectedStatuses[row.id] } : r)));
    } catch (err) {
      const message = err?.response?.data?.message || "فشل في تحديث الطلب";
      setSaveErrors((prev) => ({ ...prev, [row.id]: message }));
    } finally {
      setSavingIds((prev) => ({ ...prev, [row.id]: false }));
    }
  };

  const isLoading = isPending || fetchStatus === "fetching";

  return (
    <div dir="rtl" style={{ minHeight: "100vh", backgroundColor: "#fafafa", fontFamily: "Cairo, sans-serif" }}>
      {isLoading && (
        <Box sx={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999 }}>
          <LinearProgress />
        </Box>
      )}

      {/* Page Header */}
      <Box
        sx={{
          px: { xs: 2, md: 4 },
          pt: 3,
          pb: 2,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          borderBottom: "1.5px solid #f0f0f0",
          backgroundColor: "#fff",
          mb: 3,
        }}
      >
        <Box
          sx={{
            backgroundColor: "#fff3e0",
            borderRadius: "12px",
            p: 1,
            display: "flex",
            alignItems: "center",
          }}
        >
          <WarningAmberRoundedIcon sx={{ color: "#f57c00", fontSize: 28 }} />
        </Box>
        <Box>
          <Typography sx={{ fontFamily: "Cairo, sans-serif", fontWeight: 800, fontSize: "20px", color: "#1a1a1a" }}>الطلبات المنتهية غير المغلقة</Typography>
          <Typography sx={{ fontFamily: "Cairo, sans-serif", fontSize: "13px", color: "#999", fontWeight: 500 }}>{rows.length} طلب بحاجة إلى مراجعة</Typography>
        </Box>
      </Box>

      {/* Empty State */}
      {!isLoading && rows.length === 0 && (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" sx={{ mt: 10, gap: 2 }}>
          <Box sx={{ fontSize: "64px" }}>✅</Box>
          <Typography sx={{ fontFamily: "Cairo, sans-serif", fontWeight: 700, fontSize: "18px", color: "#555" }}>لا توجد طلبات منتهية غير مغلقة</Typography>
        </Box>
      )}

      {/* Cards Grid */}
      <Grid container spacing={2} sx={{ px: { xs: 2, md: 4 }, pb: 2 }}>
        {paginatedRows.map((row) => {
          const whatsappUrl = row.whatsapp_number ? `https://wa.me/${row.whatsapp_number}` : null;
          const currentSelected = selectedStatuses[row.id] ?? row.new_status;
          const isSaving = savingIds[row.id] || false;
          const hasChanged = currentSelected !== row.new_status;

          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={row.id}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: "20px",
                  // border: "1.5px solid #ffe0b2",
                  backgroundColor: "#fff",
                  boxShadow: "0 2px 12px rgba(245,124,0,0.07)",
                  transition: "box-shadow 0.2s, transform 0.2s",
                  "&:hover": {
                    boxShadow: "0 8px 28px rgba(245,124,0,0.15)",
                    transform: "translateY(-2px)",
                  },
                  fontFamily: "Cairo, sans-serif",
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                  {/* Header */}
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                    <Typography
                      sx={{
                        fontFamily: "Cairo, sans-serif",
                        fontWeight: 800,
                        fontSize: "0.8rem",
                        color: "#888",
                        bgcolor: "#f5f5f5",
                        px: 1,
                        py: 0.3,
                        borderRadius: 1,
                      }}
                    >
                      {row.order_number}
                    </Typography>
                    <Chip
                      label={statusMap[row.new_status]?.label ?? row.new_status}
                      color={statusMap[row.new_status]?.color ?? "default"}
                      size="small"
                      sx={{
                        fontFamily: "Cairo, sans-serif",
                        fontWeight: "700",
                        fontSize: "11px",
                        borderRadius: "8px",
                        height: "24px",
                      }}
                    />
                  </Box>

                  {/* Name + ticket icon */}
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Typography sx={{ fontFamily: "Cairo, sans-serif", fontWeight: 800, fontSize: "16px", color: "#1a1a1a" }}>{row.name || "—"}</Typography>
                    {row.images && row.images.length > 0 && (
                      <Tooltip title="تذكرة الطيران">
                        <PhotoLibraryIcon
                          sx={{ color: "#1a1a1a", cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            openTicketImage(row.images);
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>

                  {/* Phone */}
                  {row.phone && (
                    <Box mb={1.5} dir="ltr" sx={{ textAlign: "right" }}>
                      {whatsappUrl ? (
                        <a
                          href={whatsappUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: "#25D366",
                            textDecoration: "none",
                            fontWeight: "700",
                            fontSize: "14px",
                            fontFamily: "Cairo, sans-serif",
                            backgroundColor: "rgba(37,211,102,0.1)",
                            padding: "3px 10px",
                            borderRadius: "8px",
                            display: "inline-block",
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {row.phone}
                        </a>
                      ) : (
                        <Typography sx={{ fontFamily: "Cairo, sans-serif", fontSize: "14px" }}>{row.phone}</Typography>
                      )}
                    </Box>
                  )}

                  {/* Info rows */}
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", mb: 2 }}>
                    <InfoRow label="الخدمة" value={row.type} />
                    {row.extra_route_access && <InfoRow label="خدمة إضافية" value="المسار" />}
                    <InfoRow label="مكان الاستقبال" value={row.pickup_location} />
                    {row.neighborhood && row.neighborhood !== "N/A" && <InfoRow label="الحي" value={row.neighborhood} />}
                    <InfoRow label="السيارة" value={row.car_model} />
                    <InfoRow label="فندق" value={row.hotel_name} />
                    <InfoRow label="تاريخ الاستقبال" value={formatDate(row.pickup_date)} expired />
                    <InfoRow label="ساعة الوصول" value={formatHour(row.pickup_hour)} />
                    <InfoRow label="الإجمالي" value={row.price} highlight />
                    {row.driver_name && <InfoRow label="السائق" value={row.driver_name} />}
                  </Box>

                  {/* Status Select */}
                  <FormControl size="small" fullWidth>
                    <InputLabel sx={{ fontFamily: "Cairo, sans-serif", fontSize: "13px" }}>تغيير الحالة</InputLabel>
                    <Select
                      value={currentSelected}
                      label="تغيير الحالة"
                      onChange={(e) => handleStatusChange(row.id, e.target.value)}
                      sx={{
                        fontFamily: "Cairo, sans-serif",
                        borderRadius: "10px",
                        fontSize: "13px",
                        "& .MuiSelect-select": { fontFamily: "Cairo, sans-serif" },
                      }}
                    >
                      {statusOptions.map((s) => (
                        <MenuItem dir="rtl" key={s.value} value={s.value} sx={{ fontFamily: "Cairo, sans-serif", fontSize: "13px" }}>
                          {s.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </CardContent>

                {/* Save Button */}
                <CardActions sx={{ px: 2.5, pb: 2.5, pt: 0.5, flexDirection: "column" }}>
                  {saveErrors[row.id] && (
                    <Box
                      sx={{
                        width: "100%",
                        backgroundColor: "#fff3e0",
                        border: "1px solid #ffb74d",
                        borderRadius: "10px",
                        px: 1.5,
                        py: 1,
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 0.8,
                        marginBottom: "24px",
                      }}
                    >
                      <WarningAmberRoundedIcon sx={{ color: "#f57c00", fontSize: 18, mt: "1px", flexShrink: 0 }} />
                      <Typography
                        sx={{
                          fontFamily: "Cairo, sans-serif",
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#e65100",
                          lineHeight: 1.6,
                        }}
                      >
                        {saveErrors[row.id]}
                      </Typography>
                    </Box>
                  )}

                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => handleSave(row)}
                    disabled={isSaving || !hasChanged}
                    sx={{
                      backgroundColor: hasChanged ? "#f57c00" : "#e0e0e0",
                      color: hasChanged ? "#fff" : "#aaa",
                      fontFamily: "Cairo, sans-serif",
                      fontWeight: "700",
                      fontSize: "15px",
                      borderRadius: "12px",
                      py: 1,
                      boxShadow: "none",
                      transition: "background-color 0.2s",
                      "&:hover": {
                        backgroundColor: hasChanged ? "#e65100" : "#e0e0e0",
                        boxShadow: hasChanged ? "0 4px 12px rgba(245,124,0,0.35)" : "none",
                      },
                      "&.Mui-disabled": {
                        backgroundColor: "#f0f0f0",
                        color: "#bbb",
                      },
                    }}
                  >
                    {isSaving ? "جاري الحفظ..." : "حفظ التعديلات"}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Pagination */}
      {rows.length > rowsPerPage && (
        <Box display="flex" justifyContent="center" alignItems="center" mt={3} mb={4}>
          <Pagination count={Math.ceil(rows.length / rowsPerPage)} page={page} onChange={handlePageChange} color="primary" shape="rounded" hidePrevButton hideNextButton />
        </Box>
      )}
    </div>
  );
}

function InfoRow({ label, value, highlight, expired }) {
  if (!value) return null;
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        backgroundColor: expired ? "#fff3e0" : "#f7f7f7",
        borderRadius: "10px",
        px: 1.5,
        py: 0.7,
      }}
    >
      <Typography sx={{ fontFamily: "Cairo, sans-serif", fontSize: "12px", color: expired ? "#f57c00" : "#999", fontWeight: "500" }}>{label}</Typography>
      <Typography
        sx={{
          fontFamily: "Cairo, sans-serif",
          fontSize: "13px",
          fontWeight: "700",
          color: highlight ? "#000" : expired ? "#e65100" : "#1a1a1a",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}
